/**
 * The Settings page's section catalog and its whole address vocabulary
 * (FRONT-1818, epic FRONT-1318).
 *
 * Two things live here because they are one decision. The CATALOG is the
 * grouped rail — three groups, seven sections, each with its label and
 * whether its panel holds an unsaved draft. The VOCABULARY is `?section=`
 * plus the translation of every older address onto it. Splitting them would
 * let a section exist in the rail that no URL can name, or a URL name a
 * section the rail does not render, and both failures are silent.
 *
 * Shape copied from `components/sidebar-nav-catalog.ts`, which already does
 * exactly this for the main rail (a groups array, a destinations array
 * carrying each entry's group). Copied rather than imported: the destinations
 * are unrelated, and sharing the type would tie a settings section to a
 * sidebar concern like role gating it has no use for.
 *
 * ## Why the page is at `/settings`
 *
 * FRONT-1263 merged Profile, Settings and Security into `/account` and made
 * `/settings` redirect. That was backwards, and MP said so: "Settings" already
 * existed in the product meaning ORG Settings, so Eddy shipped a Settings page
 * that was not yours and an Account page that was your settings. This ticket
 * inverts it — `/settings` is the page, `/account` redirects.
 *
 * One consequence is worth stating rather than discovering: a bookmark of the
 * BARE old `/settings` used to land on the Eddy tab, because that page's own
 * landing tab was Identity. It now lands on General. That is not a regression
 * to fix — a page's canonical address has to open the page's landing section,
 * and General is where the controls a user came for actually are. Only the
 * bare shape changes; every `/settings#hash` bookmark still lands exactly
 * where it did.
 */

import { BRAND_KIT_FLAG, EDDY_MACHINE_FLAG, PRO_BUILDER_FLAG } from '~/lib/feature-flag-names';

export type SettingsGroupId = 'account' | 'eddy' | 'usage';

/**
 * Whether a flag-gated section should be offered right now (FRONT-1986).
 * `'available'` renders normally; `'hidden'` behaves exactly like a section
 * that does not exist (rail row absent, panel absent, `?section=` scrubbed);
 * `'pending'` — the flags request hasn't answered yet — keeps whatever the
 * address already asked for without redirecting, so a direct link to a
 * flagged section isn't bounced to the default the instant before the flag
 * check comes back true. A section with no `flag` is always `'available'`.
 */
export type SectionAvailability = 'available' | 'pending' | 'hidden';

export const SETTINGS_GROUPS: { id: SettingsGroupId; label: string }[] = [
  { id: 'account', label: 'Account' },
  { id: 'eddy', label: 'EDDY' },
  { id: 'usage', label: 'Usage' },
];

export interface SettingsSectionEntry {
  id: SettingsSectionId;
  /** Rail label and the content pane's own heading — one string, both places. */
  label: string;
  group: SettingsGroupId;
  /**
   * Deliberately NO icon field. The rail is text-only, which is the shape the
   * approved prototype had and which an implementation pass then broke by
   * adding icons "for consistency with the sidebar" — the wrong consistency,
   * and the reason is a rule rather than a preference:
   *
   *   - NN/g, "Local Navigation Is a Valuable Orientation and Wayfinding Aid":
   *     "local navigation should not be more salient than the global
   *     navigation, because, if that happens, users may mistake the local
   *     navigation for the global one. Visual hierarchy should reflect
   *     information hierarchy."
   *   - Adobe Spectrum's side-navigation spec is explicit about the mechanism:
   *     "In multi-level side navigation, only the first-level items can have
   *     icons. Any sub-level items should always be text-only to ensure clear
   *     hierarchy."
   *
   * The icon row was a byte-for-byte copy of the primary sidebar's nav row —
   * same padding, gap, radius, inactive colour and active pill — so the two
   * columns read as one nav system sitting side by side, which is exactly the
   * mistake NN/g names. The first person to see it said so unprompted.
   */
  /**
   * True when the panel holds an unsaved draft in component state, which is
   * what earns it the force-mount treatment in `routes/settings.tsx`. Data
   * rather than a set beside the array: a section added without thinking
   * about its drafts is then a missing property, not a silently absent
   * membership.
   */
  holdsDraft?: boolean;
  /**
   * Flag name gating this section's rail row, panel and `?section=` address
   * (FRONT-1986) — mirrors the backend flag registry, same string as the
   * frontend flag constant. Absent for every ungated section, which is why
   * this is optional rather than a `flag: string | null` on every entry.
   */
  flag?: string;
}

export const SETTINGS_SECTION_IDS = [
  'general',
  'reading',
  'identity',
  'permissions',
  'personality',
  'models',
  'knowledge',
  'design',
  'builder',
  'machine',
  'pro-builder',
  'spend',
  'workspace',
] as const;

export type SettingsSectionId = (typeof SETTINGS_SECTION_IDS)[number];

export const SETTINGS_SECTIONS: SettingsSectionEntry[] = [
  // Account
  { id: 'general', label: 'General', group: 'account' },
  // FRONT-3148: reading width and font size for every chat / Build window —
  // moved out of the windows' own "Aa" / Appearance controls.
  { id: 'reading', label: 'Reading', group: 'account' },
  { id: 'identity', label: 'Identity', group: 'account' },
  // FRONT-2221: actions you let an app's agent take without asking.
  { id: 'permissions', label: 'App permissions', group: 'account' },
  // Eddy. "Personality" rather than "Eddy": inside a group already headed
  // Eddy, a section called Eddy says nothing.
  { id: 'personality', label: 'Personality', group: 'eddy', holdsDraft: true },
  { id: 'models', label: 'Models', group: 'eddy', holdsDraft: true },
  { id: 'knowledge', label: 'Knowledge', group: 'eddy', holdsDraft: true },
  // FRONT-1824: how what EDDY makes looks for you — the workspace brand kit
  // with your own changes. Gated on the same flag as System Settings → Brand.
  { id: 'design', label: 'Design', group: 'eddy', holdsDraft: true, flag: BRAND_KIT_FLAG },
  // FRONT-3146: person-level Builder preferences, moved out of the Build
  // window's "…" menu. Not flag-gated: Build is open to everyone.
  { id: 'builder', label: 'Builder', group: 'eddy' },
  // FRONT-1986, dark release: a byo-daemon machine Eddy can route chat's
  // shell/browser tools to instead of the built-in sandbox. Gated on
  // `EDDY_MACHINE_FLAG`, which is per-PERSON on the server (not per org) —
  // see that constant's own doc comment.
  { id: 'machine', label: 'Machine', group: 'eddy', holdsDraft: true, flag: EDDY_MACHINE_FLAG },
  // FRONT-3003, dark release: how the person's own Claude Code signs in on
  // the Pro Builder window. Per-PERSON flag, same posture as Machine.
  { id: 'pro-builder', label: 'Pro Builder', group: 'eddy', holdsDraft: true, flag: PRO_BUILDER_FLAG },
  // Usage. "Workspace" rather than "Sandbox" or "Storage", and both
  // rejections matter. "Sandbox" is our word for a per-session microVM, not a
  // user's word for their disk. "Storage" was the first attempt and reads as
  // FILE storage — which this app has a whole other section for, so it invited
  // exactly the wrong guess. "Workspace" is what the panel's own loaded
  // heading already says ("Workspace storage") and what the mount path is, so
  // it is the name the product had all along.
  { id: 'spend', label: 'Compute & spend', group: 'usage' },
  { id: 'workspace', label: 'Workspace', group: 'usage' },
];

/** The section a bare `/settings` opens on. */
export const DEFAULT_SETTINGS_SECTION: SettingsSectionId = 'general';

export function isSettingsSection(
  value: string | null | undefined,
): value is SettingsSectionId {
  return !!value && (SETTINGS_SECTION_IDS as readonly string[]).includes(value);
}

const SECTION_BY_ID: ReadonlyMap<string, SettingsSectionEntry> = new Map(
  SETTINGS_SECTIONS.map((s) => [s.id, s]),
);

export function getSettingsSection(id: SettingsSectionId): SettingsSectionEntry {
  const found = SECTION_BY_ID.get(id);
  // Unreachable while `id` is typed, and a real throw rather than a fallback
  // so a section id added to the union without an entry fails loudly at the
  // first render instead of rendering an empty rail row.
  if (!found) throw new Error(`unknown settings section: ${id}`);
  return found;
}

/** The sections whose panels stay mounted once visited. */
export const DRAFT_HOLDING_SECTIONS: ReadonlySet<SettingsSectionId> = new Set(
  SETTINGS_SECTIONS.filter((s) => s.holdsDraft).map((s) => s.id),
);

/**
 * `/settings` plus the section, with the landing section left implicit so the
 * canonical URL of the page you arrive on has no query string at all.
 */
export function settingsPath(section: SettingsSectionId): string {
  return section === DEFAULT_SETTINGS_SECTION ? '/settings' : `/settings?section=${section}`;
}

/**
 * The legacy hashes that name a SECTION INSIDE a panel rather than a former
 * tab id. These are carried through a redirect (`/settings?section=personality
 * #system-prompt`) so the accordion item can be scrolled to and opened; a
 * former tab id carries no hash, because the section itself is the
 * destination.
 *
 * An anchor here is a promise that some panel actually RENDERS an element with
 * that id. Break the promise and the failure is silent in the worst way: the
 * redirect fires, the section is right, and the page sits at the top with
 * nothing scrolled to and no error to explain it. `starter-prompts` was
 * exactly that once FRONT-1319 deleted its editor. The `renders a section that
 * exists` test in `settings-sections.test.ts` checks every entry against the
 * rendered source.
 */
export const SECTION_ANCHORS = new Set([
  'eddy',
  'calibration',
  'system-prompt',
  'reflect-prompt',
]);

/**
 * Where a legacy URL HASH lands. Two kinds were in play and both have live
 * bookmarks: the pre-FRONT-1263 Settings page's own TAB ids (`#models`,
 * `#sandbox`, `#usage`, `#identity`) and SECTION anchors inside its Identity
 * tab (`#eddy`, `#system-prompt`, …), which that page scrolled into view.
 *
 * These arrive at the live page now rather than at a redirect, because
 * `/settings` is no longer a legacy path — see `resolveSettingsLocation`.
 */
export const LEGACY_HASH_TO_SECTION: Record<string, SettingsSectionId> = {
  // The old Settings page's former tab ids.
  identity: 'personality',
  models: 'models',
  knowledge: 'knowledge',
  sandbox: 'workspace',
  usage: 'spend',
  // Section anchors within its former Identity tab.
  eddy: 'personality',
  calibration: 'personality',
  'system-prompt': 'personality',
  'reflect-prompt': 'personality',
  // Two former sections are deliberately ABSENT rather than mapped:
  // `#starter-prompts` (deleted by FRONT-1319) and `#chat-design` (deleted by
  // FRONT-1782, which made the top bar the only chat design). Each falls
  // through to the landing section with no hash trailing behind to scroll at
  // something that is not there. Mapping them anyway would be worse than the
  // fallback — the setting no longer exists, so no section is the honest
  // answer and a hash would still scroll nowhere.
};

/**
 * Where an `/account?tab=` value lands. FRONT-1263's vocabulary, live for the
 * three months `/account` was the destination, so browser history is full of
 * it.
 *
 * `personal` maps to `general` rather than `identity` even though most of that
 * tab's CONTENT went to Identity. Two reasons, and the second is why it is
 * not a close call: `personal` was the default tab, so the page deleted the
 * param when it was selected and this exact string was never written into a
 * URL by the app; and the shape that IS in history — a bare `/account` — is
 * the canonical address of the destination, which has to open on its landing
 * section.
 */
export const LEGACY_TAB_TO_SECTION: Record<string, SettingsSectionId> = {
  personal: 'general',
  eddy: 'personality',
  models: 'models',
  knowledge: 'knowledge',
  sandbox: 'workspace',
  usage: 'spend',
};

/**
 * The bare legacy paths — no hash, no query — and the section each lands on.
 * Exported so the source-tree guard can tell "this link is DELIBERATELY
 * handled" apart from "this link fell through a fallback nobody thought
 * about", which is the whole difference between a guard and a decoration.
 */
export const LEGACY_BARE_PATHS: Record<string, SettingsSectionId> = {
  '/account': 'general',
  // `/profile` and `/security` both land on Identity: the name, email, user
  // id, organization, roles and active session they were each opened to see
  // are all in that one section now. FRONT-1819 sent both to Personal, which
  // held those rows at the time; this ticket split that tab and Identity is
  // the half that kept them.
  '/profile': 'identity',
  '/security': 'identity',
};

/**
 * Literal regexes rather than one helper taking the character: a
 * `new RegExp` built from a non-literal is flagged as a ReDoS risk by the
 * security scan, and two three-character patterns earn nothing by sharing.
 */
const stripQuestion = (value: string | undefined): string => (value ?? '').replace(/^\?/, '');
const stripHash = (value: string | undefined): string => (value ?? '').replace(/^#/, '');

/**
 * The legacy tables are declared as object literals above because that is the
 * readable form for a lookup table someone has to maintain — but they are only
 * ever READ through these Maps, never indexed directly.
 *
 * A plain object literal indexed with a URL fragment resolves through
 * `Object.prototype`: `/settings#constructor` returns a native function,
 * `/settings#__proto__` returns `Object.prototype`, and both are TRUTHY, so a
 * `?? fallback` never fires and the "section" that comes out is not a section
 * id at all. It reached `settingsPath()`, which stringified it into the
 * redirect path, and `Tabs value=` for one render. It self-heals in one more
 * hop and throws nothing, so the only tell was that the module's own
 * "never resolves to a section the page cannot render" test did not include a
 * single `Object.prototype` member name in its adversarial corpus — it does
 * now, and the corpus is what proves this fix rather than the Maps themselves.
 *
 * A `Map` rather than `Object.hasOwn` at each call site: the guard then cannot
 * be forgotten at a fourth lookup added later, and `SECTION_BY_ID` above
 * already establishes the pattern in this file.
 */
function bareSection(path: '/account' | '/profile' | '/security'): SettingsSectionId {
  // The keys are a closed literal union, so this cannot miss — the accessor
  // exists so no caller is tempted to index the record with a value that came
  // off a URL, which is the mistake the Maps below exist to make impossible.
  return LEGACY_BARE_PATHS[path];
}

const HASH_TO_SECTION: ReadonlyMap<string, SettingsSectionId> = new Map(
  Object.entries(LEGACY_HASH_TO_SECTION),
);
const TAB_TO_SECTION: ReadonlyMap<string, SettingsSectionId> = new Map(
  Object.entries(LEGACY_TAB_TO_SECTION),
);
const BARE_PATH_TO_SECTION: ReadonlyMap<string, SettingsSectionId> = new Map(
  Object.entries(LEGACY_BARE_PATHS),
);

/**
 * A canonical `/settings` address for a section, with a section anchor riding
 * along ONLY when that anchor belongs to that section.
 *
 * Both halves of the condition are load-bearing. `SECTION_ANCHORS` is a flat
 * global set, so on its own it answers "is this a real anchor somewhere",
 * which is the wrong question: all four of today's anchors live in the
 * Personality panel, and `legacySettingsTarget` resolves the section from
 * `?tab=` FIRST. So `/account?tab=knowledge#calibration` used to produce
 * `/settings?section=knowledge#calibration` — a section that renders and an
 * anchor it does not contain, and because that address carries a VALID
 * `?section=` the resolver reads it as already canonical and never corrects
 * it. The scroll then silently no-ops forever.
 *
 * No in-app link ever paired a non-Personality tab with one of these anchors,
 * so this was reachable only by hand-typing or a mangled bookmark — the reason
 * it is a consistency fix rather than an urgent one. It is still worth closing:
 * it removes an asymmetry between this function and `resolveSettingsLocation`,
 * which for a direct `/settings#calibration` visit has always let the HASH
 * pick the section and so could never disagree with itself.
 */
function anchorBelongsTo(cleanHash: string, section: SettingsSectionId): boolean {
  return SECTION_ANCHORS.has(cleanHash) && HASH_TO_SECTION.get(cleanHash) === section;
}

function targetFor(section: SettingsSectionId, cleanHash: string): string {
  const base = settingsPath(section);
  return anchorBelongsTo(cleanHash, section) ? `${base}#${cleanHash}` : base;
}

/**
 * Translate a visit to an old `/account`, `/profile` or `/security` URL into
 * its `/settings` equivalent.
 *
 * `search` and `hash` are taken as raw location strings (leading `?` / `#`
 * optional) so a caller can hand this `useLocation()` straight through.
 * Anything unrecognised falls back to that path's own entry in
 * `LEGACY_BARE_PATHS` — never a 404, and never a section the page cannot
 * render.
 */
export function legacySettingsTarget(
  pathname: string,
  search: string | undefined,
  hash: string | undefined,
): string {
  const cleanHash = stripHash(hash);

  // `/profile` and `/security` never addressed anything but their own page and
  // (for Profile) the Security tab that FRONT-1819 emptied, so the query and
  // the hash carry nothing worth reading. Ignoring the hash is deliberate
  // rather than lazy: carrying one would aim a scroll at an id Identity does
  // not render.
  if (pathname.startsWith('/profile')) return settingsPath(bareSection('/profile'));
  if (pathname.startsWith('/security')) return settingsPath(bareSection('/security'));

  // `/account` — FRONT-1263's `?tab=`, with a section anchor able to ride
  // along (`/account?tab=eddy#system-prompt` was a live shape).
  const tab = new URLSearchParams(stripQuestion(search)).get('tab');
  const section =
    (tab ? TAB_TO_SECTION.get(tab) : undefined) ??
    HASH_TO_SECTION.get(cleanHash) ??
    bareSection('/account');
  return targetFor(section, cleanHash);
}

/**
 * What the live `/settings` page should show for a given address, and whether
 * that address needs rewriting first.
 *
 * One function rather than three effects on the page, because the three
 * questions interact: which section to render, whether a legacy hash has to be
 * lifted into a `?section=`, and whether a param the page cannot render has to
 * be scrubbed out of the URL. Answering them separately is how two of them
 * fight — a scrub that fires on the same render as a lift wipes the anchor the
 * lift just set. It is also the one shape this file's own corpus test can run
 * over every address at once.
 *
 * `redirectTo` is null when the address is already canonical. A non-null value
 * is always a full path, hash included, for `navigate(..., { replace: true })`
 * — never a search-params patch, which cannot clear a hash.
 *
 * `opts.availability` (FRONT-1986) answers, for a flag-gated section, whether
 * it is `'available'`, `'pending'` (the flag check hasn't answered yet) or
 * `'hidden'`. Omitted entirely, every section reads as `'available'` — the
 * byte-identical behaviour every pre-FRONT-1986 caller and test already
 * relies on. A requested section that is `'hidden'` is treated exactly like
 * one `isSettingsSection` would reject (falls through to the legacy-hash /
 * default handling below, redirect included); `'pending'` short-circuits to
 * "render what was asked for, redirect nothing" — bouncing a direct link to
 * a flagged section to the default the instant before its flag check lands
 * would be worse than a brief blank pane.
 */
export interface ResolveSettingsLocationOptions {
  availability?: (id: SettingsSectionId) => SectionAvailability;
}

export function resolveSettingsLocation(
  search: string | undefined,
  hash: string | undefined,
  opts?: ResolveSettingsLocationOptions,
): { section: SettingsSectionId; redirectTo: string | null } {
  const raw = new URLSearchParams(stripQuestion(search)).get('section');
  const cleanHash = stripHash(hash);
  const availabilityOf = (id: SettingsSectionId): SectionAvailability =>
    opts?.availability ? opts.availability(id) : 'available';

  // A section the page can render is canonical — as long as any anchor riding
  // with it belongs to that section. `?section=general` alone is left exactly
  // as typed (rendering what the URL says is never wrong, and rewriting it
  // would fight a hand-typed link for no gain), but
  // `?section=general#system-prompt` is not canonical: that anchor lives in
  // the Personality panel, so General renders and the scroll silently no-ops
  // forever, since a valid `?section=` used to short-circuit this branch
  // before the hash was ever looked at.
  //
  // This is the same rule `targetFor` applies on the legacy path, and it
  // reads the same predicate rather than a second copy — the asymmetry
  // between the two was the finding (DerStoffel on PR #1345), and one shared
  // predicate is what stops it coming back.
  if (isSettingsSection(raw)) {
    const availability = availabilityOf(raw);
    if (availability === 'pending') {
      // The flag check hasn't answered yet — render what was asked for and
      // redirect nothing. Falling through to "unknown section" here would
      // bounce a direct link to a flagged section to the default the instant
      // before the flag comes back true, which is worse than a brief blank
      // pane (the rail/panel lists render nothing for a 'pending' section
      // either, per `useSectionAvailability`'s callers).
      return { section: raw, redirectTo: null };
    }
    if (availability === 'available') {
      const keepAnchor = cleanHash === '' || anchorBelongsTo(cleanHash, raw);
      return {
        section: raw,
        redirectTo: keepAnchor ? null : settingsPath(raw),
      };
    }
    // 'hidden' — behave exactly like a section `isSettingsSection` rejects:
    // fall through to the legacy-hash / default handling below, so the
    // address is scrubbed rather than left claiming a section the page will
    // not render (same FRONT-1819 rule the default branch already applies).
  }

  // No usable param, so a legacy hash gets to decide — and gets lifted into
  // the param, which is what stops this from re-firing: the lifted URL takes
  // the branch above.
  const fromHash = HASH_TO_SECTION.get(cleanHash);
  if (fromHash) return { section: fromHash, redirectTo: targetFor(fromHash, cleanHash) };

  // Nothing usable at all. Land on the default, and scrub anything the page
  // cannot honour out of the address rather than merely ignoring it
  // (FRONT-1819's rule): a URL left saying `?section=security` shows General
  // while claiming otherwise, so a copy-paste or a re-bookmark propagates the
  // dead name further.
  const stray = raw !== null || cleanHash !== '';
  return {
    section: DEFAULT_SETTINGS_SECTION,
    redirectTo: stray ? settingsPath(DEFAULT_SETTINGS_SECTION) : null,
  };
}
